import React, { useEffect, useState } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { Search, UserCircle, Phone, Mail, MapPin, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '@/utils/api';
import { showError } from '@/utils/toast';

const Customers = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await api.get('/leads');
      // Group by mobile number to identify unique customers
      const uniqueCustomers = [];
      const seen = new Set();
      response.data.forEach((lead) => {
        if (!seen.has(lead.mobile_number)) {
          seen.add(lead.mobile_number);
          uniqueCustomers.push(lead);
        }
      });
      setLeads(uniqueCustomers);
    } catch (error) {
      showError('Failed to fetch customers');
    } finally {
      setLoading(false);
    }
  };

  const filtered = leads.filter(
    (c) =>
      c.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.mobile_number?.includes(searchTerm) ||
      (c.email && c.email.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="animate-spin text-primary" size={40} />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div>
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">Customers</h1>
          <p className="text-muted-foreground">View all customer profiles</p>
        </div>

        <div className="bg-card border border-border rounded-xl p-6 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={20} />
            <input
              type="text"
              data-testid="search-customers"
              placeholder="Search customers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 outline-none"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.length === 0 ? (
            <div className="col-span-3 text-center py-12 text-muted-foreground">No customers found</div>
          ) : (
            filtered.map((customer) => (
              <Link
                key={customer.id}
                to={`/leads/${customer.id}`}
                data-testid={`customer-card-${customer.id}`}
                className="bg-card border border-border rounded-xl p-5 hover:shadow-lg hover:border-primary transition-all"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-primary/10 text-primary flex items-center justify-center font-semibold">
                    {customer.customer_name?.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-foreground truncate">{customer.customer_name}</h3>
                    <p className="text-xs text-muted-foreground capitalize">{customer.loan_type} Loan</p>
                  </div>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Phone size={14} />
                    <span>{customer.mobile_number}</span>
                  </div>
                  {customer.email && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Mail size={14} />
                      <span className="truncate">{customer.email}</span>
                    </div>
                  )}
                  {customer.city && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <MapPin size={14} />
                      <span>{customer.city}, {customer.state}</span>
                    </div>
                  )}
                </div>
              </Link>
            ))
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Customers;
